import { useEffect, useState, type ReactNode } from 'react';
import { useAuthStore } from '../stores/auth';
import { SkeletonCard, SkeletonText } from '../components/Skeleton';
import { api } from '../utils/api-client';
import { LineChart } from '../components/LineChart';

interface AnalyticsSummary {
  clicks: { total: number; unique: number };
  conversions: { total: number; rate: number };
  revenue: { total: number; commission: number };
  commission: { pending: number; approved: number };
  dateRange: { start: string; end: string };
}

interface ClicksPeriod {
  period: string;
  total: number;
  unique: number;
}

interface ConversionsPeriod {
  period: string;
  count: number;
  revenue: number;
  commission: number;
}

interface TopLink {
  id: string;
  name: string;
  code: string;
  clicks: number;
  conversions: number;
  conversionRate: number;
}

type DateRangeOption = '7d' | '30d' | '90d' | 'custom';
type Interval = 'day' | 'week' | 'month';

function getDateRange(option: DateRangeOption): { startDate: string; endDate: string } {
  const now = new Date();
  const endDate = now.toISOString().split('T')[0] ?? '';
  let start: Date;

  switch (option) {
    case '7d':
      start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case '30d':
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case '90d':
      start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      break;
    default:
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  }

  return {
    startDate: start.toISOString().split('T')[0] ?? '',
    endDate,
  };
}

export function AnalyticsPage(): ReactNode {
  const { accessToken } = useAuthStore();
  const [dateRange, setDateRange] = useState<DateRangeOption>('30d');
  const [interval, setInterval] = useState<Interval>('day');
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [clicksData, setClicksData] = useState<ClicksPeriod[]>([]);
  const [conversionsData, setConversionsData] = useState<ConversionsPeriod[]>([]);
  const [topLinks, setTopLinks] = useState<TopLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetchAnalytics();
  }, [accessToken, dateRange, interval]);

  const fetchAnalytics = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    const { startDate, endDate } = getDateRange(dateRange);
    const queryParams = new URLSearchParams({
      startDate,
      endDate,
      interval,
    });

    try {
      // Fetch all data in parallel with proper error handling
      const [summaryData, clicksDataRes, conversionsDataRes, topLinksDataRes] = await Promise.allSettled([
        api.get<AnalyticsSummary>(`/affiliate/analytics/summary?${queryParams}`).catch(() => null),
        api.get<{ periods: ClicksPeriod[] }>(`/affiliate/analytics/clicks?${queryParams}`).catch(() => ({ periods: [] })),
        api.get<{ periods: ConversionsPeriod[] }>(`/affiliate/analytics/conversions?${queryParams}`).catch(() => ({ periods: [] })),
        api.get<{ links: TopLink[] }>(`/affiliate/analytics/top-links?limit=10`).catch(() => ({ links: [] })),
      ]);

      // Handle summary data
      if (summaryData.status === 'fulfilled' && summaryData.value !== null) {
        setSummary(summaryData.value);
      } else {
        setSummary(null);
      }

      // Handle clicks data
      if (clicksDataRes.status === 'fulfilled' && clicksDataRes.value) {
        setClicksData(clicksDataRes.value.periods);
      } else {
        setClicksData([]);
      }

      // Handle conversions data
      if (conversionsDataRes.status === 'fulfilled' && conversionsDataRes.value) {
        setConversionsData(conversionsDataRes.value.periods);
      } else {
        setConversionsData([]);
      }

      // Handle top links data
      if (topLinksDataRes.status === 'fulfilled' && topLinksDataRes.value) {
        setTopLinks(topLinksDataRes.value.links);
      } else {
        setTopLinks([]);
      }

      // Set error if all requests failed
      if (summaryData.status === 'rejected' && 
          clicksDataRes.status === 'rejected' && 
          conversionsDataRes.status === 'rejected' && 
          topLinksDataRes.status === 'rejected') {
        setError('Unable to load analytics data. Please try again later.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <SkeletonText width="w-32" size="lg" />
            <div className="mt-2">
              <SkeletonText width="w-64" size="sm" />
            </div>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Analytics</h2>
          <p className="text-gray-500">
            Track your affiliate performance and revenue
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={interval}
            onChange={(e) => setInterval(e.target.value as Interval)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
          </select>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as DateRangeOption)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
      </div>

      {error !== null && (
        <div className="rounded-lg bg-red-50 p-4 text-red-700">{error}</div>
      )}

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Clicks"
          value={formatNumber(summary?.clicks.total ?? 0)}
          subValue={`${formatNumber(summary?.clicks.unique ?? 0)} unique`}
          icon={
            <svg className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
            </svg>
          }
        />
        <StatCard
          title="Conversions"
          value={formatNumber(summary?.conversions.total ?? 0)}
          subValue={`${summary?.conversions.rate ?? 0}% rate`}
          icon={
            <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <StatCard
          title="Total Revenue"
          value={formatCurrency(summary?.revenue.total ?? 0)}
          subValue={`${formatCurrency(summary?.revenue.commission ?? 0)} commission`}
          icon={
            <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <StatCard
          title="Pending Commission"
          value={formatCurrency(summary?.commission.pending ?? 0)}
          subValue={`${formatCurrency(summary?.commission.approved ?? 0)} approved`}
          icon={
            <svg className="h-5 w-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
      </div>

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Clicks Chart */}
        <div className="rounded-lg border bg-white p-6">
          <h3 className="text-lg font-semibold text-gray-900">Clicks Over Time</h3>
          <p className="mt-1 text-sm text-gray-500">Total and unique clicks</p>
          <div className="mt-4 h-64">
            <LineChart
              data={clicksData.map((d) => ({
                label: formatPeriodLabel(d.period, interval),
                value: d.total,
                secondaryValue: d.unique,
              }))}
              primaryColor="#8b5cf6"
              secondaryColor="#c4b5fd"
              primaryLabel="Total"
              secondaryLabel="Unique"
            />
          </div>
        </div>

        {/* Revenue Chart */}
        <div className="rounded-lg border bg-white p-6">
          <h3 className="text-lg font-semibold text-gray-900">Revenue Over Time</h3>
          <p className="mt-1 text-sm text-gray-500">Revenue and commission earned</p>
          <div className="mt-4 h-64">
            <LineChart
              data={conversionsData.map((d) => ({
                label: formatPeriodLabel(d.period, interval),
                value: d.revenue,
                secondaryValue: d.commission,
              }))}
              primaryColor="#3b82f6"
              secondaryColor="#93c5fd"
              primaryLabel="Revenue"
              secondaryLabel="Commission"
              formatValue={formatCurrency}
            />
          </div>
        </div>
      </div>

      {/* Conversions Chart */}
      <div className="rounded-lg border bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900">Conversions Over Time</h3>
        <p className="mt-1 text-sm text-gray-500">Number of conversions per period</p>
        <div className="mt-4 h-64">
          <LineChart
            data={conversionsData.map((d) => ({
              label: formatPeriodLabel(d.period, interval),
              value: d.count,
            }))}
            primaryColor="#22c55e"
            primaryLabel="Conversions"
          />
        </div>
      </div>

      {/* Conversion Funnel */}
      <div className="rounded-lg border bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900">Conversion Funnel</h3>
        <p className="mt-1 text-sm text-gray-500">Visualize your conversion flow</p>
        <div className="mt-6">
          <ConversionFunnel
            clicks={summary?.clicks.total ?? 0}
            conversions={summary?.conversions.total ?? 0}
            revenue={summary?.revenue.total ?? 0}
          />
        </div>
      </div>

      {/* Conversion Funnel */}
      <div className="rounded-lg border bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900">Conversion Funnel</h3>
        <p className="mt-1 text-sm text-gray-500">Visualize your conversion flow</p>
        <div className="mt-6">
          <ConversionFunnel
            clicks={summary?.clicks.total ?? 0}
            conversions={summary?.conversions.total ?? 0}
            revenue={summary?.revenue.total ?? 0}
          />
        </div>
      </div>

      {/* Top Links */}
      <div className="rounded-lg border bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900">Top Performing Links</h3>
        <p className="mt-1 text-sm text-gray-500">Your best performing affiliate links</p>

        {topLinks.length === 0 ? (
          <div className="mt-6 rounded-lg border-2 border-dashed border-gray-200 p-8 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">No links yet</h3>
            <p className="mt-2 text-gray-500">
              Create your first affiliate link to start tracking performance
            </p>
            <a
              href="/links"
              className="mt-4 inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              Create Link
            </a>
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b text-left text-sm font-medium text-gray-500">
                  <th className="pb-3 pr-4">Link</th>
                  <th className="pb-3 pr-4 text-right">Clicks</th>
                  <th className="pb-3 pr-4 text-right">Conversions</th>
                  <th className="pb-3 text-right">Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {topLinks.map((link) => (
                  <tr key={link.id} className="text-sm">
                    <td className="py-3 pr-4">
                      <p className="font-medium text-gray-900">{link.name}</p>
                      <p className="text-xs text-gray-500">{link.code}</p>
                    </td>
                    <td className="py-3 pr-4 text-right text-gray-700">
                      {formatNumber(link.clicks)}
                    </td>
                    <td className="py-3 pr-4 text-right text-gray-700">
                      {formatNumber(link.conversions)}
                    </td>
                    <td className="py-3 text-right">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          link.conversionRate >= 5
                            ? 'bg-green-100 text-green-700'
                            : link.conversionRate >= 2
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {link.conversionRate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Conversion Funnel Component
function ConversionFunnel({
  clicks,
  conversions,
  revenue,
}: {
  clicks: number;
  conversions: number;
  revenue: number;
}): ReactNode {
  const formatCurrencyLocal = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const clickRate = clicks > 0 ? (conversions / clicks) * 100 : 0;
  const avgOrderValue = conversions > 0 ? revenue / conversions : 0;

  const stages = [
    { label: 'Clicks', value: clicks, color: '#8b5cf6', width: 100 },
    { label: 'Conversions', value: conversions, color: '#3b82f6', width: Math.max(10, (conversions / Math.max(1, clicks)) * 100) },
    { label: 'Revenue', value: revenue, color: '#22c55e', width: Math.max(10, (revenue / Math.max(1, clicks * 10)) * 100) },
  ];

  return (
    <div className="space-y-4">
      {stages.map((stage, i) => (
        <div key={stage.label}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">{stage.label}</span>
            <span className="text-sm text-gray-500">
              {stage.label === 'Revenue'
                ? formatCurrencyLocal(stage.value)
                : stage.value.toLocaleString()}
              {i > 0 && (
                <span className="ml-2 text-xs text-gray-400">
                  ({((stage.value / Math.max(1, stages[i - 1]?.value ?? 1)) * 100).toFixed(1)}%)
                </span>
              )}
            </span>
          </div>
          <div className="h-8 rounded-lg overflow-hidden bg-gray-100">
            <div
              className="h-full transition-all duration-500 flex items-center justify-end pr-2"
              style={{
                width: `${stage.width}%`,
                backgroundColor: stage.color,
              }}
            >
              {stage.width > 20 && (
                <span className="text-xs font-medium text-white">
                  {stage.label === 'Revenue'
                    ? formatCurrencyLocal(stage.value)
                    : stage.value.toLocaleString()}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
      <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">{clickRate.toFixed(2)}%</div>
          <div className="text-xs text-gray-500">Conversion Rate</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">{formatCurrencyLocal(avgOrderValue)}</div>
          <div className="text-xs text-gray-500">Avg Order Value</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            {clicks > 0 ? formatCurrencyLocal(revenue / clicks) : '$0.00'}
          </div>
          <div className="text-xs text-gray-500">EPC</div>
        </div>
      </div>
    </div>
  );
}

// Helper function to format period labels
function formatPeriodLabel(period: string, interval: Interval): string {
  if (interval === 'month') {
    const parts = period.split('-');
    const year = parts[0] ?? '2024';
    const month = parts[1] ?? '01';
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short' });
  }
  if (interval === 'week') {
    const weekParts = period.split('-');
    return `W${weekParts[1] ?? '1'}`;
  }
  // Day format
  const date = new Date(period);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Stat Card Component
function StatCard({
  title,
  value,
  subValue,
  icon,
}: {
  title: string;
  value: string;
  subValue?: string;
  icon: ReactNode;
}): ReactNode {
  return (
    <div className="rounded-lg border bg-white p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        {icon}
      </div>
      <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
      {subValue !== undefined && (
        <p className="mt-1 text-sm text-gray-500">{subValue}</p>
      )}
    </div>
  );
}

